import React from 'react';
import { useLibkeyResult } from '../components/LibkeyProvider';
import {
  LibkeyAlertType,
  LibkeyReferenceId,
} from 'query/study/LibKey/Articles';
import { Link, Message, Text } from 'components/core';
import { Inline } from 'components/layout';

// Copy shown for each of the three LibKey alert states. The visual treatment is
// identical for all three (see article_result.md); only the heading differs.
const ALERT_MESSAGE: Record<LibkeyAlertType, string> = {
  article_retracted: 'This article has been retracted',
  expression_of_concern: 'This article has an expression of concern',
  problematic_journal: 'This article is associated with a problematic journal',
};

type LibkeyAlertBannerProps = {
  referenceId: LibkeyReferenceId;
  /** Extra class for the banner wrapper, e.g. to add spacing in a given list. */
  className?: string;
};

/**
 * Warning banner for a reference whose LibKey result carries an `alert`
 * (:article_retracted, :expression_of_concern, :problematic_journal). These
 * three states used to render as a warning variant of the LibkeyLink; they
 * are now surfaced as this banner instead, while the link keeps handling the
 * non-alert access types.
 *
 * Renders nothing unless LibKey is enabled and the reference resolved to a
 * result with a non-null `alert` — safe to mount for every reference. The
 * "View notice" link points at the LibKey notice page (`result.url`); it is
 * omitted only in the (unexpected) case of an alert result with no url.
 */
export const LibkeyAlertBanner = ({
  referenceId,
  className,
}: LibkeyAlertBannerProps) => {
  const { isEnabled, result } = useLibkeyResult(referenceId);

  if (!isEnabled || !result?.alert) {
    return null;
  }

  return (
    <Message
      type="warning"
      variant="prominent"
      size="sm"
      dismissible={false}
      presentational // Advisory, static retraction notice rather than a time-critical alert.
      className={className}
      data-testid="libkey-alert-banner"
    >
      <Inline alignX="spaceBetween" alignY="center" spacing={3}>
        <Text>{ALERT_MESSAGE[result.alert]}</Text>
        {result.url && (
          <Link external to={result.url}>
            View notice
          </Link>
        )}
      </Inline>
    </Message>
  );
};
